/** Shared copy for notification list rows + toast (keep in sync visually). */

import { resolveAvatarUrl } from "../../utils/sessionUser";
import {
  formatTraderDailyLeaderboardXpNotificationDetail,
  formatSixesDailyLeaderboardXpNotificationDetail,
} from "../../utils/xp";
import { CRICKET8_LOGO_SRC } from "../Logo";
import { formatMatchNameAbbreviated } from "../../utils/matchNameDisplay";
import {
  isFriendChallengeHistory,
  isFriendChallengeMatchOpen,
  isFriendChallengePlayable,
} from "./notificationTaxonomy";

export function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const sec = Math.floor((Date.now() - d) / 1000);
  if (sec < 60) return "now";
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
  const nowY = new Date().getFullYear();
  return d.getFullYear() === nowY
    ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function senderProfileTo(notification) {
  const s = notification.sender;
  if (s?.player_id) return `/profile?p=${encodeURIComponent(s.player_id)}`;
  if (s?.userId) return `/profile?u=${s.userId}`;
  return "/profile";
}

export function parseXpWeekPodiumEntityRef(ref) {
  try {
    const o = JSON.parse(ref || "{}");
    const rank = parseInt(String(o.rank), 10);
    const xpValue = parseInt(String(o.xp_value), 10);
    const tiedCount = parseInt(String(o.tied_count), 10);
    return {
      periodLabel: typeof o.period_label === "string" ? o.period_label.trim() : "",
      rank: Number.isFinite(rank) && rank > 0 ? rank : null,
      rankLabel: typeof o.rank_label === "string" ? o.rank_label.trim() : "",
      xpValue: Number.isFinite(xpValue) && xpValue >= 0 ? xpValue : null,
      crown: typeof o.crown === "string" ? o.crown.trim() : "",
      tiedCount: Number.isFinite(tiedCount) && tiedCount > 0 ? tiedCount : 1,
    };
  } catch {
    return { periodLabel: "", rank: null, rankLabel: "", xpValue: null, crown: "", tiedCount: 1 };
  }
}

/**
 * `entity_ref` JSON for `admin_message` (Player Communications Center broadcasts):
 * `{ title, body, cta_label?, cta_href?, broadcast_id? }`. All fields are admin-authored.
 */
export function parseAdminMessageEntityRef(ref) {
  try {
    const o = JSON.parse(ref || "{}");
    const title = typeof o.title === "string" ? o.title.trim() : "";
    const body = typeof o.body === "string" ? o.body.trim() : "";
    const ctaLabel = typeof o.cta_label === "string" ? o.cta_label.trim() : "";
    const ctaHrefRaw = typeof o.cta_href === "string" ? o.cta_href.trim() : "";
    // Only accept internal paths — never navigate to an external/absolute URL from a notification.
    const ctaHref = ctaHrefRaw.startsWith("/") && !ctaHrefRaw.startsWith("//") ? ctaHrefRaw : "";
    return { title, body, ctaLabel, ctaHref };
  } catch {
    return { title: "", body: "", ctaLabel: "", ctaHref: "" };
  }
}

/**
 * `entity_ref` JSON for `wallet_grant` (admin currency gifts):
 * `{ title, body, coins, rubies }`.
 */
export function parseWalletGrantEntityRef(ref) {
  try {
    const o = JSON.parse(ref || "{}");
    const coins = parseInt(String(o.coins), 10);
    const rubies = parseInt(String(o.rubies), 10);
    return {
      title: typeof o.title === "string" ? o.title.trim() : "",
      body: typeof o.body === "string" ? o.body.trim() : "",
      coins: Number.isFinite(coins) && coins > 0 ? coins : 0,
      rubies: Number.isFinite(rubies) && rubies > 0 ? rubies : 0,
    };
  } catch {
    return { title: "", body: "", coins: 0, rubies: 0 };
  }
}

const WALLET_GRANT_DEFAULT_TITLE = "You received a gift!";

/** Title segments for wallet gifts — amounts with currency icons when present. */
export function walletGrantTitleParts({ title, body, coins, rubies }) {
  const parts = [];
  const customTitle = title && title !== WALLET_GRANT_DEFAULT_TITLE ? title : "";
  if (customTitle) {
    parts.push({ text: customTitle, emphasize: true });
    parts.push({ text: " " });
  }

  if (coins > 0 || rubies > 0) {
    parts.push({ text: customTitle ? "We've added " : "You've been gifted " });
    if (coins > 0) {
      parts.push({ text: String(coins), emphasize: true });
      parts.push({ coinIcon: true });
    }
    if (coins > 0 && rubies > 0) {
      parts.push({ text: " and " });
    }
    if (rubies > 0) {
      parts.push({ text: String(rubies), emphasize: true });
      parts.push({ rubyIcon: true });
    }
    parts.push({ text: " to your wallet." });
    return parts;
  }

  if (body) {
    parts.push({ text: body });
    return parts;
  }

  if (parts.length === 0) {
    parts.push({ text: "You received a gift in your wallet." });
  }
  return parts;
}

export function parseXpEntityRef(ref) {
  try {
    const o = JSON.parse(ref || "{}");
    const amount = parseInt(String(o.amount), 10);
    const grantCount = parseInt(String(o.grant_count), 10);
    return {
      amount: Number.isFinite(amount) ? amount : 0,
      label: typeof o.label === "string" ? o.label.trim() : "",
      reason: typeof o.reason === "string" ? o.reason : "",
      grantCount: Number.isFinite(grantCount) && grantCount > 0 ? grantCount : null,
      batched: o.batched === true,
    };
  } catch {
    return { amount: 0, label: "", reason: "", grantCount: null, batched: false };
  }
}

/**
 * Phrase after "for …" in XP earned notifications.
 * @param {string|null|undefined} reason
 * @param {string} [label]
 * @param {number|null} [grantCount]
 * @param {{ forFriend?: boolean }} [opts] — friend activity must avoid second-person "your".
 */
export function xpNotificationDetail(reason, label, grantCount = null, opts = {}) {
  const forFriend = opts?.forFriend === true;
  const base = String(reason ?? "")
    .trim()
    .split(":")[0];
  if (base === "xp_friends_batch") {
    if (grantCount != null && grantCount > 1) {
      return `${grantCount} activities`;
    }
    return "recent activity";
  }
  if (base === "wheel_multiplier_home" || base === "wheel_multiplier_away") {
    return "winning in Wheel Trivia multiplier";
  }
  if (base === "trader_win") {
    return "winning a Trader game";
  }
  if (base === "trader_all_in_first") {
    return forFriend ? "a first all-in Trader game" : "your first all-in Trader game";
  }
  if (base.startsWith("trader_match_wins")) {
    return "winning multiple Trader games in one match";
  }
  if (base === "trader_win_streak_4" || base === "trader_win_streak_8") {
    return "a Trader win streak";
  }
  if (base.startsWith("trader_daily_coins_first")) {
    return formatTraderDailyLeaderboardXpNotificationDetail(reason);
  }
  if (base.startsWith("sixes_daily_coins_first")) {
    return formatSixesDailyLeaderboardXpNotificationDetail(reason);
  }
  if (base === "login_daily" || base.startsWith("login_daily:")) {
    return "visiting Cricket8 today";
  }
  if (base === "login_streak_4" || base === "login_streak_8") {
    return forFriend ? "a daily visit streak" : "your daily visit streak";
  }
  if (base.startsWith("squares_play_over")) {
    return "playing a Squares over";
  }
  if (base.startsWith("squares_win_over")) {
    return "winning a Squares over";
  }
  if (base.startsWith("squares_five_balls")) {
    return "getting 5 balls right in Squares";
  }
  if (base.startsWith("squares_perfect_over")) {
    return "a perfect Squares over";
  }
  if (base.startsWith("bullion_play_private")) {
    return "playing a private Bullion game";
  }
  if (base.startsWith("bullion_play_public")) {
    return "playing a public Bullion game";
  }
  if (base.startsWith("bullion_win_private_first")) {
    return "winning a private Bullion game";
  }
  if (base.startsWith("bullion_win_public_first")) {
    return "winning a public Bullion game";
  }
  if (base.startsWith("bullion_place_private")) {
    return "placing in a private Bullion game";
  }
  if (base.startsWith("bullion_place_public")) {
    return "placing in a public Bullion game";
  }
  if (base.startsWith("bullion_predictions_80pct")) {
    return "80%+ correct picks in Bullion";
  }
  if (base.startsWith("shop_rubies_3")) {
    return "purchasing a 3-pack of Rubies";
  }
  if (base.startsWith("shop_rubies_10")) {
    return "purchasing a 10-pack of Rubies";
  }
  if (base.startsWith("shop_rubies_25")) {
    return "purchasing a 25-pack of Rubies";
  }
  if (base.startsWith("shop_feature_pack")) {
    return "unlocking the Profile Unlock Pack";
  }
  if (base === "shop_first_ruby_pack") {
    return forFriend ? "a first Ruby pack purchase" : "your first Ruby pack purchase";
  }
  if (base === "social_first_friend") {
    return forFriend ? "adding a first friend" : "adding your first friend";
  }
  if (base.startsWith("social_invite_new_user")) {
    return "inviting a new user";
  }
  if (base.startsWith("social_friend_existing")) {
    return "making a new friend";
  }
  if (base === "social_favourite_team") {
    return forFriend
      ? "adding a favourite team to the profile"
      : "adding a favourite team to your profile";
  }
  if (base === "social_favourite_player") {
    return forFriend
      ? "adding a favourite player to the profile"
      : "adding a favourite player to your profile";
  }
  if (base === "social_favourite_sport") {
    return forFriend
      ? "adding a favourite sport to the profile"
      : "adding a favourite sport to your profile";
  }
  if (base === "social_country") {
    return forFriend
      ? "adding a country to the profile"
      : "adding your country to your profile";
  }
  if (base === "social_player_name") {
    return forFriend ? "creating a player name" : "creating your player name";
  }
  if (base === "social_avatar") {
    return forFriend ? "choosing an avatar" : "choosing your avatar";
  }
  if (base.startsWith("quickfire_correct")) {
    return "a correct Quickfire answer";
  }
  if (base === "quickfire_daily_perfect" || base.startsWith("quickfire_daily_perfect:")) {
    return "getting every daily Quickfire question right";
  }
  if (base === "wheel_spin_daily") {
    return forFriend ? "Daily Free Gold Balls" : "your Daily Free Gold Balls";
  }
  if (base.startsWith("wheel_multiplier_streak")) {
    return forFriend
      ? "a Wheel Trivia multiplier streak"
      : "your Wheel Trivia multiplier streak";
  }
  if (label) return label.charAt(0).toLowerCase() + label.slice(1);
  return "activity";
}

function playRouteForType(type) {
  if (!type) return "/trader";
  const t = String(type).toLowerCase();
  if (t.includes("squares")) return "/squares";
  if (t.includes("bullion")) return "/bullion";
  if (t.includes("league")) return "/leagues";
  if (t.includes("quickfire")) return "/quickfire";
  return "/trader";
}

function isPlayInviteType(type) {
  if (!type) return false;
  if (type === "trader_game_starting") return true;
  const t = String(type);
  return t.endsWith("_invite") || t.includes("challenge") || t.includes("game_invite");
}

/** True when notification sender should show the lime “online” dot (API may send `is_online`). */
export function notificationSenderAppearsOnline(sender) {
  if (!sender || typeof sender !== "object") return false;
  if (typeof sender.is_online === "boolean") return sender.is_online;
  if (typeof sender.online === "boolean") return sender.online;
  if (typeof sender.online_status === "boolean") return sender.online_status;
  if (typeof sender.online_status === "string") {
    const status = sender.online_status.trim().toLowerCase();
    if (status === "online") return true;
    if (status === "offline") return false;
  }
  if (typeof sender.presence === "boolean") return sender.presence;
  if (sender.presence && typeof sender.presence === "object" && typeof sender.presence.online === "boolean") {
    return sender.presence.online;
  }
  return false;
}

export function parseSquaresChallengeEntityRef(ref) {
  if (ref == null || typeof ref !== "string") return { matchId: null, matchName: null };
  try {
    const o = JSON.parse(ref);
    return {
      matchId: o.match_id != null ? String(o.match_id) : null,
      matchName: o.match_name != null ? String(o.match_name) : null,
    };
  } catch {
    return { matchId: null, matchName: null };
  }
}

/** `entity_ref` JSON for `sixes_card_ready`: `{ title, team_a, team_b, match_name, cta_href, cta_label }`. */
export function parseSixesCardReadyEntityRef(ref) {
  if (ref != null && typeof ref === "object") return ref;
  try {
    const o = JSON.parse(ref || "{}");
    return o && typeof o === "object" && !Array.isArray(o) ? o : {};
  } catch {
    return {};
  }
}

/** `entity_ref` JSON for `bullion_match_challenge` / `bullion_match_challenge_sent`. */
export function parseBullionChallengeEntityRef(ref) {
  if (ref == null || typeof ref !== "string") {
    return { matchId: null, matchName: null, gameId: null, gameName: null, inviteCode: null, visibility: null };
  }
  try {
    const o = JSON.parse(ref);
    const gid = o.game_id != null ? parseInt(String(o.game_id), 10) : NaN;
    const visRaw =
      o.visibility != null && String(o.visibility).trim() !== ""
        ? String(o.visibility).trim().toLowerCase()
        : null;
    const visibility = visRaw === "public" || visRaw === "private" ? visRaw : null;
    return {
      matchId: o.match_id != null ? String(o.match_id) : null,
      matchName: o.match_name != null ? String(o.match_name) : null,
      gameId: Number.isFinite(gid) ? gid : null,
      gameName: o.game_name != null ? String(o.game_name) : null,
      inviteCode:
        o.invite_code != null && String(o.invite_code).trim() !== "" ? String(o.invite_code).trim() : null,
      visibility,
    };
  } catch {
    return { matchId: null, matchName: null, gameId: null, gameName: null, inviteCode: null, visibility: null };
  }
}

/** Public arenas also carry `game_id`; prefer `visibility` from entity_ref (backend), then legacy invite-code hint. */
export function isBullionPrivateBattleChallenge(parsed) {
  if (!parsed || typeof parsed !== "object") return false;
  if (parsed.visibility === "public") return false;
  if (parsed.visibility === "private") return true;
  return Boolean(parsed.inviteCode && String(parsed.inviteCode).trim());
}

/** `entity_ref` JSON for league invite / join / starting / cancelled notifications. */
function normalizeLeagueCancelReason(raw) {
  const key = String(raw ?? "").trim().toLowerCase();
  if (key === "underfill" || key === "abandoned" || key === "admin") return key;
  return null;
}

export function parseLeagueInviteEntityRef(ref) {
  if (ref == null || typeof ref !== "string") {
    return {
      leagueId: null,
      leagueName: null,
      code: null,
      matchName: null,
      matchLive: false,
      rubiesRefunded: false,
      cancelReason: null,
      place: null,
      placeLabel: null,
      prizeCoins: 0,
      xpEarned: 0,
    };
  }
  try {
    const o = JSON.parse(ref);
    const leagueId = o.league_id != null && String(o.league_id).trim() ? String(o.league_id).trim() : null;
    const placeRaw = parseInt(String(o.place ?? ""), 10);
    const place = Number.isFinite(placeRaw) && placeRaw > 0 ? placeRaw : null;
    const prizeRaw = parseInt(String(o.prize_coins ?? ""), 10);
    const prizeCoins = Number.isFinite(prizeRaw) && prizeRaw > 0 ? prizeRaw : 0;
    const xpRaw = parseInt(String(o.xp_earned ?? ""), 10);
    const xpEarned = Number.isFinite(xpRaw) && xpRaw > 0 ? xpRaw : 0;
    const placeLabel =
      typeof o.place_label === "string" && o.place_label.trim()
        ? o.place_label.trim().toLowerCase()
        : place != null
          ? formatLeaguePlaceLabel(place)
          : null;
    return {
      leagueId,
      leagueName: o.league_name != null && String(o.league_name).trim() ? String(o.league_name).trim() : null,
      code: o.code != null && String(o.code).trim() ? String(o.code).trim() : null,
      matchName: o.match_name != null && String(o.match_name).trim() ? String(o.match_name).trim() : null,
      matchLive: o.match_live === true,
      rubiesRefunded: o.rubies_refunded === true,
      cancelReason: normalizeLeagueCancelReason(o.reason),
      place,
      placeLabel,
      prizeCoins,
      xpEarned,
    };
  } catch {
    return {
      leagueId: null,
      leagueName: null,
      code: null,
      matchName: null,
      matchLive: false,
      rubiesRefunded: false,
      cancelReason: null,
      place: null,
      placeLabel: null,
      prizeCoins: 0,
      xpEarned: 0,
    };
  }
}

/** Lowercase ordinal for league finish copy, e.g. "1st" / "2nd". */
function formatLeaguePlaceLabel(place) {
  const n = Math.max(0, Math.floor(Number(place) || 0));
  if (n <= 0) return "";
  const mod100 = n % 100;
  let suffix = "th";
  if (mod100 < 11 || mod100 > 13) {
    const mod10 = n % 10;
    if (mod10 === 1) suffix = "st";
    else if (mod10 === 2) suffix = "nd";
    else if (mod10 === 3) suffix = "rd";
  }
  return `${n}${suffix}`;
}

export function leagueInviteDeepLinkFromEntityRef(entityRef) {
  const { leagueId, code } = parseLeagueInviteEntityRef(entityRef);
  if (leagueId) return `/leagues?id=${encodeURIComponent(leagueId)}`;
  if (code) return `/leagues?code=${encodeURIComponent(code)}`;
  return "/leagues";
}

/** `entity_ref` JSON for `sixes_match_challenge` / `_sent` — `{ match_id, match_name }`. */
export function parseSixesChallengeEntityRef(ref) {
  if (ref == null || typeof ref !== "string") {
    return { matchId: null, matchName: null };
  }
  try {
    const o = JSON.parse(ref);
    return {
      matchId: o.match_id != null ? String(o.match_id) : null,
      matchName: o.match_name != null ? String(o.match_name) : null,
    };
  } catch {
    return { matchId: null, matchName: null };
  }
}

/** `entity_ref` JSON for `trader_match_challenge` / `_sent` — `{ match_id, match_name, sport }`. */
export function parseTraderChallengeEntityRef(ref) {
  if (ref == null || typeof ref !== "string") {
    return { matchId: null, matchName: null, sport: null };
  }
  try {
    const o = JSON.parse(ref);
    return {
      matchId: o.match_id != null ? String(o.match_id) : null,
      matchName: o.match_name != null ? String(o.match_name) : null,
      sport: o.sport != null ? String(o.sport).trim().toLowerCase() : null,
    };
  } catch {
    return { matchId: null, matchName: null, sport: null };
  }
}

export function sixesMatchDeepLinkFromEntityRef(entityRef) {
  const { matchId } = parseSixesChallengeEntityRef(entityRef);
  if (!matchId) return "/sixes";
  return `/sixes?match=${encodeURIComponent(matchId)}`;
}

export function traderMatchDeepLinkFromEntityRef(entityRef) {
  const { matchId, sport } = parseTraderChallengeEntityRef(entityRef);
  if (!matchId) return "/trader";
  const params = new URLSearchParams();
  const sportKey = sport || "cricket";
  params.set("sport", sportKey);
  if (sportKey === "horses") {
    params.set("raceId", matchId);
  } else {
    params.set("matchId", matchId);
  }
  return `/trader?${params.toString()}`;
}

/** Deep link for My Zone / notifications CTA — keep in sync with `getNotificationDisplay` bullion cases. */
export function bullionContestDeepLinkFromEntityRef(entityRef) {
  const { matchId, gameId, inviteCode } = parseBullionChallengeEntityRef(entityRef);
  if (!matchId || gameId == null || Number.isNaN(gameId)) return "/bullion";
  const q = new URLSearchParams();
  q.set("match", matchId);
  q.set("game", String(gameId));
  if (inviteCode) q.set("invite", inviteCode);
  return `/bullion?${q.toString()}`;
}

/**
 * Title line segments. `emphasizeUsername` → Anton 9px uppercase (friend/sender name). `emphasize` → Chakra emphasis (amounts, match labels, etc.). `coinIcon` / `rubyIcon` render currency images inline.
 * @returns {{ titleParts: Array<{ text?: string, emphasize?: boolean, emphasizeUsername?: boolean, rubyIcon?: boolean, coinIcon?: boolean }>, avatarUrl: string | null, avatarObjectFit?: 'cover' | 'contain', cta: { label: string, to: string, openShopDrawer?: boolean, openWalletHistory?: boolean } | null, navigateTo: string }}
 */
export function getNotificationDisplay(notification) {
  const senderName = notification.sender
    ? notification.sender.player_name || notification.sender.username || "Someone"
    : "Cricket8";
  const profileTo = senderProfileTo(notification);
  const senderPic = resolveAvatarUrl(notification.sender);

  switch (notification.type) {
    case "xp_earned": {
      const { amount, label, reason, grantCount } = parseXpEntityRef(notification.entity_ref);
      const detail = xpNotificationDetail(reason, label, grantCount);
      return {
        titleParts: [
          { text: "You earned " },
          { text: String(amount || 0), emphasize: true },
          { xpIcon: true },
          { text: ` for ${detail}.` },
        ],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "XP", to: "/profile" },
        navigateTo: "/profile",
      };
    }
    case "friend_earned_xp": {
      const { amount, label, reason, grantCount } = parseXpEntityRef(notification.entity_ref);
      const detail = xpNotificationDetail(reason, label, grantCount, { forFriend: true });
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " earned " },
          { text: String(amount || 0), emphasize: true },
          { xpIcon: true },
          { text: ` for ${detail}.` },
        ],
        avatarUrl: senderPic,
        cta: { label: "PROFILE", to: profileTo },
        navigateTo: profileTo,
      };
    }
    case "xp_week_podium": {
      const { periodLabel, rankLabel, xpValue } = parseXpWeekPodiumEntityRef(notification.entity_ref);
      const place = rankLabel || "on the podium";
      const weekLabel = periodLabel || "last week";
      const titleParts = [
        { text: "You finished " },
        { text: place, emphasize: true },
        { text: " on the XP leaderboard for " },
        { text: weekLabel, emphasize: true },
        { text: "." },
      ];
      if (xpValue != null) {
        titleParts.push(
          { text: " (" },
          { text: String(xpValue), emphasize: true },
          { xpIcon: true },
          { text: ")." }
        );
      }
      return {
        titleParts,
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "LEADERBOARD", to: "/xp-leaderboard" },
        navigateTo: "/xp-leaderboard",
      };
    }
    case "welcome_bonus":
      return {
        titleParts: [
          { text: "Welcome to Cricket8", emphasize: true },
          { text: " 🎉" },
          { text: " You've been gifted " },
          { text: "1000 ", emphasize: true },
          { coinIcon: true },
          { text: " and " },
          { text: "1 ", emphasize: true },
          { rubyIcon: true },
          { text: " to play our games." },
        ],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "SHOP", to: "/shop", openShopDrawer: true },
        navigateTo: "/",
      };
    case "friend_request":
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " sent you a friend request" },
        ],
        avatarUrl: senderPic,
        cta: { label: "PROFILE", to: profileTo },
        navigateTo: profileTo,
      };
    case "friend_request_accepted":
    case "now_friends":
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " is now your friend." },
        ],
        avatarUrl: senderPic,
        cta: { label: "PROFILE", to: profileTo },
        navigateTo: profileTo,
      };
    case "trader_game_starting":
      return {
        titleParts: [{ text: "Trader game is starting" }],
        avatarUrl: senderPic,
        cta: { label: "PLAY", to: "/trader" },
        navigateTo: "/trader",
      };
    case "squares_match_challenge": {
      const { matchId, matchName } = parseSquaresChallengeEntityRef(notification.entity_ref);
      const label = formatMatchNameAbbreviated(matchName) || "a live match";
      const playTo = matchId
        ? `/squares?match=${encodeURIComponent(matchId)}`
        : "/squares";
      const playable = isFriendChallengePlayable(notification);
      const matchOpen = isFriendChallengeMatchOpen(notification);
      const history = isFriendChallengeHistory(notification);
      // History (entered match) keeps invite copy; closed match adds finished suffix.
      const closedSuffix = !matchOpen && !history ? ". That match has finished." : ".";
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " challenged you to play Squares on " },
          { text: label, emphasize: true },
          { text: closedSuffix },
        ],
        avatarUrl: senderPic,
        cta: playable ? { label: "PLAY SQUARES", to: playTo } : null,
        // History can still reopen a live match; finished rows go to profile.
        navigateTo: matchOpen ? playTo : profileTo,
      };
    }
    case "squares_match_challenge_sent": {
      const { matchId, matchName } = parseSquaresChallengeEntityRef(notification.entity_ref);
      const label = formatMatchNameAbbreviated(matchName) || "a live match";
      const playTo = matchId
        ? `/squares?match=${encodeURIComponent(matchId)}`
        : "/squares";
      const playable = isFriendChallengePlayable(notification);
      const matchOpen = isFriendChallengeMatchOpen(notification);
      const history = isFriendChallengeHistory(notification);
      const closedSuffix = !matchOpen && !history ? ". That match has finished." : ".";
      return {
        titleParts: [
          { text: "You challenged " },
          { text: senderName, emphasizeUsername: true },
          { text: " to play Squares on " },
          { text: label, emphasize: true },
          { text: closedSuffix },
        ],
        avatarUrl: senderPic,
        cta: playable ? { label: "MATCH", to: playTo } : null,
        navigateTo: matchOpen ? playTo : profileTo,
      };
    }
    case "bullion_match_challenge": {
      const parsed = parseBullionChallengeEntityRef(notification.entity_ref);
      const { matchName, gameName } = parsed;
      const matchLabel = formatMatchNameAbbreviated(matchName) || "this match";
      const gameLabel = gameName && gameName.trim() ? gameName.trim() : "";
      const playTo = bullionContestDeepLinkFromEntityRef(notification.entity_ref);
      const playable = isFriendChallengePlayable(notification);
      const matchOpen = isFriendChallengeMatchOpen(notification);
      const history = isFriendChallengeHistory(notification);
      const isPrivateBattle = isBullionPrivateBattleChallenge(parsed);
      const inviteVerbPhrase = isPrivateBattle
        ? " invited you to the private battle "
        : " invited you to the public arena ";
      const closedSuffixText = isPrivateBattle
        ? ". That battle has closed."
        : ". That arena has closed.";
      const closedSuffix = !matchOpen && !history ? closedSuffixText : ".";
      const challengeLine = [
        { text: senderName, emphasizeUsername: true },
        { text: inviteVerbPhrase },
        ...(gameLabel ? [{ text: gameLabel, emphasize: true }, { text: " on " }] : [{ text: "on " }]),
        { text: matchLabel, emphasize: true },
        { text: closedSuffix },
      ];
      return {
        titleParts: challengeLine,
        avatarUrl: senderPic,
        cta: playable ? { label: "PLAY BULLION", to: playTo } : null,
        navigateTo: matchOpen ? playTo : profileTo,
      };
    }
    case "bullion_match_challenge_sent": {
      const parsed = parseBullionChallengeEntityRef(notification.entity_ref);
      const { matchName, gameName } = parsed;
      const matchLabel = formatMatchNameAbbreviated(matchName) || "this match";
      const gameLabel = gameName && gameName.trim() ? gameName.trim() : "";
      const playTo = bullionContestDeepLinkFromEntityRef(notification.entity_ref);
      const playable = isFriendChallengePlayable(notification);
      const matchOpen = isFriendChallengeMatchOpen(notification);
      const history = isFriendChallengeHistory(notification);
      const isPrivateBattle = isBullionPrivateBattleChallenge(parsed);
      const inviteVerbPhrase = isPrivateBattle
        ? " to the private battle "
        : " to the public arena ";
      const closedSuffixText = isPrivateBattle
        ? ". That battle has closed."
        : ". That arena has closed.";
      const closedSuffix = !matchOpen && !history ? closedSuffixText : ".";
      const sentLine = [
        { text: "You invited " },
        { text: senderName, emphasizeUsername: true },
        { text: inviteVerbPhrase },
        ...(gameLabel ? [{ text: gameLabel, emphasize: true }, { text: " on " }] : [{ text: "on " }]),
        { text: matchLabel, emphasize: true },
        { text: closedSuffix },
      ];
      return {
        titleParts: sentLine,
        avatarUrl: senderPic,
        cta: playable ? { label: "OPEN", to: playTo } : null,
        navigateTo: matchOpen ? playTo : profileTo,
      };
    }
    case "league_invite": {
      const { leagueName, matchName, matchLive } = parseLeagueInviteEntityRef(notification.entity_ref);
      const playTo = leagueInviteDeepLinkFromEntityRef(notification.entity_ref);
      const nameLabel = leagueName || "a league";
      const matchLabel = formatMatchNameAbbreviated(matchName);
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " invited you to " },
          { text: nameLabel, emphasize: true },
          ...(matchLabel ? [{ text: " on " }, { text: matchLabel, emphasize: true }] : []),
          { text: matchLive ? ". The match is live — you can still join." : "." },
        ],
        avatarUrl: senderPic,
        cta: { label: "JOIN LEAGUE", to: playTo },
        navigateTo: playTo,
      };
    }
    case "bullion_battle_entered": {
      const parsed = parseBullionChallengeEntityRef(notification.entity_ref);
      const { matchName, gameName } = parsed;
      const matchLabel = formatMatchNameAbbreviated(matchName) || "this match";
      const gameLabel = gameName && gameName.trim() ? gameName.trim() : "";
      const playTo = bullionContestDeepLinkFromEntityRef(notification.entity_ref);
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " entered your private battle " },
          ...(gameLabel ? [{ text: gameLabel, emphasize: true }, { text: " on " }] : [{ text: "on " }]),
          { text: matchLabel, emphasize: true },
          { text: "." },
        ],
        avatarUrl: senderPic,
        cta: { label: "OPEN", to: playTo },
        navigateTo: playTo,
      };
    }
    case "league_member_joined": {
      const { leagueName } = parseLeagueInviteEntityRef(notification.entity_ref);
      const playTo = leagueInviteDeepLinkFromEntityRef(notification.entity_ref);
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " joined " },
          { text: leagueName || "your league", emphasize: true },
          { text: "." },
        ],
        avatarUrl: senderPic,
        cta: { label: "OPEN", to: playTo },
        navigateTo: playTo,
      };
    }
    case "league_member_joined_live": {
      const { leagueName } = parseLeagueInviteEntityRef(notification.entity_ref);
      const playTo = leagueInviteDeepLinkFromEntityRef(notification.entity_ref);
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " joined " },
          { text: leagueName || "your league", emphasize: true },
          { text: " mid-match." },
        ],
        avatarUrl: senderPic,
        cta: { label: "OPEN", to: playTo },
        navigateTo: playTo,
      };
    }
    case "league_invite_sent": {
      const { leagueName, matchName } = parseLeagueInviteEntityRef(notification.entity_ref);
      const playTo = leagueInviteDeepLinkFromEntityRef(notification.entity_ref);
      const nameLabel = leagueName || "a league";
      const matchLabel = formatMatchNameAbbreviated(matchName);
      return {
        titleParts: [
          { text: "You invited " },
          { text: senderName, emphasizeUsername: true },
          { text: " to " },
          { text: nameLabel, emphasize: true },
          ...(matchLabel ? [{ text: " on " }, { text: matchLabel, emphasize: true }] : []),
          { text: "." },
        ],
        avatarUrl: senderPic,
        cta: { label: "OPEN", to: playTo },
        navigateTo: playTo,
      };
    }
    case "league_starting": {
      const { leagueName, matchName } = parseLeagueInviteEntityRef(notification.entity_ref);
      const playTo = leagueInviteDeepLinkFromEntityRef(notification.entity_ref);
      const nameLabel = leagueName || "your league";
      const matchLabel = formatMatchNameAbbreviated(matchName);
      return {
        titleParts: [
          { text: nameLabel, emphasize: true },
          { text: " starts soon" },
          ...(matchLabel ? [{ text: " — " }, { text: matchLabel, emphasize: true }] : []),
          { text: "." },
        ],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "OPEN LEAGUE", to: playTo },
        navigateTo: playTo,
      };
    }
    case "league_cancelled": {
      const { leagueName, rubiesRefunded, cancelReason } = parseLeagueInviteEntityRef(
        notification.entity_ref,
      );
      const playTo = leagueInviteDeepLinkFromEntityRef(notification.entity_ref);
      const nameLabel = leagueName || "Your league";
      const reasonBit =
        cancelReason === "underfill"
          ? " — not enough players joined"
          : cancelReason === "abandoned"
            ? " — the match was abandoned"
            : "";
      const refundBit = rubiesRefunded
        ? reasonBit
          ? ". Your Rubies were returned."
          : " — your Rubies were returned."
        : ".";
      return {
        titleParts: [
          { text: nameLabel, emphasize: true },
          { text: " was cancelled" },
          { text: `${reasonBit}${refundBit}` },
        ],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "VIEW LEAGUE", to: playTo },
        navigateTo: playTo,
      };
    }
    case "league_finished": {
      const { leagueName, placeLabel, prizeCoins, xpEarned } = parseLeagueInviteEntityRef(
        notification.entity_ref,
      );
      const playTo = leagueInviteDeepLinkFromEntityRef(notification.entity_ref);
      const nameLabel = leagueName || "your league";
      /** @type {Array<{ text?: string, emphasize?: boolean, coinIcon?: boolean, xpIcon?: boolean }>} */
      const titleParts = placeLabel
        ? [
            { text: "You finished " },
            { text: placeLabel, emphasize: true },
            { text: " in " },
            { text: nameLabel, emphasize: true },
          ]
        : [
            { text: nameLabel, emphasize: true },
            { text: " has finished" },
          ];
      if (prizeCoins > 0 || xpEarned > 0) {
        titleParts.push({ text: ". You received " });
        if (prizeCoins > 0) {
          titleParts.push(
            { text: prizeCoins.toLocaleString("en-US"), emphasize: true },
            { text: " " },
            { coinIcon: true },
          );
        }
        if (prizeCoins > 0 && xpEarned > 0) {
          titleParts.push({ text: " and " });
        }
        if (xpEarned > 0) {
          titleParts.push(
            { text: xpEarned.toLocaleString("en-US"), emphasize: true },
            { text: " " },
            { xpIcon: true },
          );
        }
        titleParts.push({ text: "." });
      } else {
        titleParts.push({ text: ". Results are ready." });
      }
      return {
        titleParts,
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "SEE RESULTS", to: playTo },
        navigateTo: playTo,
      };
    }
    case "sixes_match_challenge": {
      const { matchId, matchName } = parseSixesChallengeEntityRef(notification.entity_ref);
      const label = formatMatchNameAbbreviated(matchName) || "a live match";
      const playTo = matchId ? sixesMatchDeepLinkFromEntityRef(notification.entity_ref) : "/sixes";
      const playable = isFriendChallengePlayable(notification);
      const matchOpen = isFriendChallengeMatchOpen(notification);
      const history = isFriendChallengeHistory(notification);
      const closedSuffix = !matchOpen && !history ? ". That match has finished." : ".";
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: " challenged you to play Sixes on " },
          { text: label, emphasize: true },
          { text: closedSuffix },
        ],
        avatarUrl: senderPic,
        cta: playable ? { label: "PLAY SIXES", to: playTo } : null,
        navigateTo: matchOpen ? playTo : profileTo,
      };
    }
    case "sixes_match_challenge_sent": {
      const { matchName } = parseSixesChallengeEntityRef(notification.entity_ref);
      const label = formatMatchNameAbbreviated(matchName) || "a live match";
      const playTo = sixesMatchDeepLinkFromEntityRef(notification.entity_ref);
      const playable = isFriendChallengePlayable(notification);
      const matchOpen = isFriendChallengeMatchOpen(notification);
      const history = isFriendChallengeHistory(notification);
      const closedSuffix = !matchOpen && !history ? ". That match has finished." : ".";
      return {
        titleParts: [
          { text: "You challenged " },
          { text: senderName, emphasizeUsername: true },
          { text: " to play Sixes on " },
          { text: label, emphasize: true },
          { text: closedSuffix },
        ],
        avatarUrl: senderPic,
        cta: playable ? { label: "MATCH", to: playTo } : null,
        navigateTo: matchOpen ? playTo : profileTo,
      };
    }
    case "trader_match_challenge": {
      const { matchName, sport } = parseTraderChallengeEntityRef(notification.entity_ref);
      const label = formatMatchNameAbbreviated(matchName) || "a live match";
      const playTo = traderMatchDeepLinkFromEntityRef(notification.entity_ref);
      const playable = isFriendChallengePlayable(notification);
      const matchOpen = isFriendChallengeMatchOpen(notification);
      const history = isFriendChallengeHistory(notification);
      const closedSuffix = !matchOpen && !history ? ". That match has finished." : ".";
      const sportBit = sport ? ` (${sport})` : "";
      return {
        titleParts: [
          { text: senderName, emphasizeUsername: true },
          { text: ` challenged you to trade${sportBit} on ` },
          { text: label, emphasize: true },
          { text: closedSuffix },
        ],
        avatarUrl: senderPic,
        cta: playable ? { label: "TRADE", to: playTo } : null,
        navigateTo: matchOpen ? playTo : profileTo,
      };
    }
    case "trader_match_challenge_sent": {
      const { matchName, sport } = parseTraderChallengeEntityRef(notification.entity_ref);
      const label = formatMatchNameAbbreviated(matchName) || "a live match";
      const playTo = traderMatchDeepLinkFromEntityRef(notification.entity_ref);
      const playable = isFriendChallengePlayable(notification);
      const matchOpen = isFriendChallengeMatchOpen(notification);
      const history = isFriendChallengeHistory(notification);
      const closedSuffix = !matchOpen && !history ? ". That match has finished." : ".";
      const sportBit = sport ? ` (${sport})` : "";
      return {
        titleParts: [
          { text: "You challenged " },
          { text: senderName, emphasizeUsername: true },
          { text: ` to trade${sportBit} on ` },
          { text: label, emphasize: true },
          { text: closedSuffix },
        ],
        avatarUrl: senderPic,
        cta: playable ? { label: "MATCH", to: playTo } : null,
        navigateTo: matchOpen ? playTo : profileTo,
      };
    }
    case "coins_running_low":
      return {
        titleParts: [
          { text: "Your gold balls are running low." },
          { text: " Top up in the Shop to keep playing." },
        ],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "SHOP", to: "/shop", openShopDrawer: true },
        navigateTo: "/",
      };
    case "unlock_username_emoji":
      return {
        titleParts: [
          { text: "Unlock your " },
          { text: "username / emoji", emphasize: true },
          { text: " pack to personalise your profile." },
        ],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "MY ACCOUNT", to: "/account" },
        navigateTo: "/account",
      };
    case "promo":
      return {
        titleParts: [{ text: "New promo is live. Check it out now." }],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "PLAY", to: "/" },
        navigateTo: "/",
      };
    case "sixes_card_ready": {
      const ref = parseSixesCardReadyEntityRef(notification.entity_ref);
      const teamA = typeof ref.team_a === "string" ? ref.team_a.trim() : "";
      const teamB = typeof ref.team_b === "string" ? ref.team_b.trim() : "";
      const matchTitle =
        typeof ref.title === "string" && ref.title.trim()
          ? ref.title.trim()
          : teamA && teamB
            ? `${teamA} v ${teamB}`
            : typeof ref.match_name === "string" && ref.match_name.trim()
              ? ref.match_name.trim()
              : "today";
      const ctaHref =
        typeof ref.cta_href === "string" && ref.cta_href.trim() ? ref.cta_href.trim() : "/sixes";
      const ctaLabel =
        typeof ref.cta_label === "string" && ref.cta_label.trim()
          ? ref.cta_label.trim().slice(0, 18)
          : "OPEN SIXES";
      return {
        titleParts: [
          { text: "Your Sixes card for " },
          { text: matchTitle, emphasize: true },
          { text: " is ready to reveal." },
        ],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: ctaLabel, to: ctaHref },
        navigateTo: ctaHref,
      };
    }
    case "sixes_cards_online": {
      const ref = parseSixesCardReadyEntityRef(notification.entity_ref);
      const teamA = typeof ref.team_a === "string" ? ref.team_a.trim() : "";
      const teamB = typeof ref.team_b === "string" ? ref.team_b.trim() : "";
      const matchTitle =
        typeof ref.title === "string" && ref.title.trim()
          ? ref.title.trim()
          : teamA && teamB
            ? `${teamA} v ${teamB}`
            : "";
      const ctaHref =
        typeof ref.cta_href === "string" && ref.cta_href.trim() ? ref.cta_href.trim() : "/sixes";
      const ctaLabel =
        typeof ref.cta_label === "string" && ref.cta_label.trim()
          ? ref.cta_label.trim().slice(0, 18)
          : "OPEN SIXES";
      return {
        titleParts: matchTitle
          ? [
              { text: "Sixes cards are online — " },
              { text: matchTitle, emphasize: true },
              { text: " is ready to play." },
            ]
          : [{ text: "Sixes cards are online. Open the lobby to play." }],
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: ctaLabel, to: ctaHref },
        navigateTo: ctaHref,
      };
    }
    case "playable_game_offer": {
      const ref = parseSixesCardReadyEntityRef(notification.entity_ref);
      const gameTypeRaw = typeof ref.game_type === "string" ? ref.game_type.trim().toLowerCase() : "";
      const gameType =
        gameTypeRaw === "bullion" ? "bullion" : gameTypeRaw === "leagues" ? "leagues" : "sixes";
      const matchTitle =
        typeof ref.title === "string" && ref.title.trim()
          ? ref.title.trim()
          : typeof ref.match_name === "string" && ref.match_name.trim()
            ? ref.match_name.trim()
            : "today";
      const ctaHrefRaw = typeof ref.cta_href === "string" ? ref.cta_href.trim() : "";
      const ctaHref =
        ctaHrefRaw.startsWith("/") && !ctaHrefRaw.startsWith("//")
          ? ctaHrefRaw
          : gameType === "bullion"
            ? "/bullion"
            : gameType === "leagues"
              ? "/leagues"
              : "/sixes";
      const ctaLabel =
        typeof ref.cta_label === "string" && ref.cta_label.trim()
          ? ref.cta_label.trim().slice(0, 18)
          : gameType === "bullion"
            ? "MAKE PICKS"
            : gameType === "leagues"
              ? "JOIN LEAGUE"
              : "REVEAL CARD";
      const lead =
        gameType === "bullion"
          ? "Make your picks in Bullion — "
          : gameType === "leagues"
            ? "Join the public league — "
            : "Reveal your card in Sixes — ";
      return {
        titleParts: [{ text: lead }, { text: matchTitle, emphasize: true }],
        avatarUrl:
          gameType === "bullion"
            ? "/assets/bullion.png"
            : gameType === "leagues"
              ? "/assets/league-trophy.png"
              : "/assets/sixes.png",
        avatarObjectFit: "contain",
        cta: { label: ctaLabel, to: ctaHref },
        navigateTo: ctaHref,
      };
    }
    case "wallet_grant": {
      const grant = parseWalletGrantEntityRef(notification.entity_ref);
      return {
        titleParts: walletGrantTitleParts(grant),
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta: { label: "WALLET", to: "/", openWalletHistory: true },
        navigateTo: "/",
      };
    }
    case "admin_message": {
      const { title, body, ctaLabel, ctaHref } = parseAdminMessageEntityRef(notification.entity_ref);
      const titleParts = [];
      if (title) titleParts.push({ text: title, emphasize: true });
      if (title && body) titleParts.push({ text: " " });
      if (body) titleParts.push({ text: body });
      if (titleParts.length === 0) titleParts.push({ text: "You have a new message from Cricket8." });
      const opensShop = /^\/shop(?:[/?#]|$)/.test(ctaHref);
      const cta =
        ctaLabel && ctaHref
          ? { label: ctaLabel.slice(0, 18), to: ctaHref, ...(opensShop && { openShopDrawer: true }) }
          : null;
      return {
        titleParts,
        avatarUrl: CRICKET8_LOGO_SRC,
        avatarObjectFit: "contain",
        cta,
        navigateTo: opensShop ? "/" : ctaHref || "/",
      };
    }
    default:
      if (isPlayInviteType(notification.type)) {
        const to = playRouteForType(notification.type);
        const raw = notification.type?.replace(/_/g, " ") || "New notification";
        return {
          titleParts: [{ text: raw }],
          avatarUrl: senderPic,
          cta: { label: "PLAY", to },
          navigateTo: to,
        };
      }
      return {
        titleParts: [{ text: notification.type?.replace(/_/g, " ") || "New notification" }],
        avatarUrl: senderPic,
        cta: null,
        navigateTo: "/profile",
      };
  }
}

/** Outer shell stays overflow-visible so the online presence dot (-top/-right) is not clipped. */
export const NOTIFICATION_AVATAR_CLASS =
  "relative h-[30px] w-[30px] shrink-0 self-start bg-[#d379f6]/25";

/** Same 30×30 as default (friends activity / compact). */
export const NOTIFICATION_AVATAR_CLASS_COMPACT =
  "relative h-[30px] w-[30px] shrink-0 self-start bg-[#d379f6]/25";

/** Activity stream avatar / logo — vertically centered with body copy (same 30×30 as notification rows). */
export const NOTIFICATION_AVATAR_STREAM_CLASS =
  "relative h-[30px] w-[30px] shrink-0 self-center bg-[#d379f6]/25";

/** Padding for the title column (notification drawer, toast, activity stream). */
export const NOTIFICATION_MAIN_TEXT_PAD = "min-h-0 py-0 pl-0.5 pr-1 sm:pl-1 sm:pr-1.5";

/** CTA pill — same as notification drawer / list rows. */
export const NOTIFICATION_CTA_PILL_CLASS =
  "inline-flex h-[22px] min-w-[4rem] w-[4rem] max-h-[22px] shrink-0 items-center justify-center whitespace-nowrap rounded-none border-0 bg-white px-1.5 py-0 text-center font-anton text-[9px] font-normal uppercase leading-none tracking-wide text-black transition-opacity hover:opacity-90 sm:min-w-[5.25rem] sm:w-[5.25rem] sm:px-2 sm:text-[10px]";

/**
 * Activity stream rows: same 3-column grid as notification items; auto height, body vertically centered with avatar/time.
 */
export const NOTIFICATION_ACTIVITY_STREAM_ROW_GRID_CLASS =
  "grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-0 overflow-x-hidden bg-[#7234c1] pr-1 transition-[filter] hover:brightness-105 sm:pr-2";

/** Primary body copy — friend activity stream (slightly larger than notification title line). */
export const NOTIFICATION_STREAM_BODY_CLASS =
  "min-w-0 break-words font-chakra text-[12px] font-normal leading-[18px] tracking-normal text-white sm:text-[13px] sm:leading-[20px]";

/** Emphasised name — friend activity stream. */
export const NOTIFICATION_STREAM_NAME_CLASS =
  "font-anton text-[10px] font-normal leading-[18px] tracking-normal uppercase sm:text-[11px] sm:leading-[20px]";

export const NOTIFICATION_STREAM_TIME_SIDE_CLASS =
  "whitespace-nowrap text-[11px] leading-none text-[#c4b8d4] sm:text-[12px]";

export const NOTIFICATION_STREAM_TIME_SUBLINE_CLASS =
  "mt-0.5 text-[11px] leading-tight text-[#c4b8d4] sm:text-[12px]";

/**
 * My Zone desktop activity panel — row, avatar, type, and CTA sizing.
 * Min-height matches avatar; row grows when copy wraps (long names / multi-line PB text).
 */
export const MY_ZONE_ACTIVITY_PANEL_ROW_GRID_CLASS =
  "grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 overflow-x-hidden bg-[#1B062F]/30 min-h-[40px] pr-2 transition-[filter] hover:brightness-105";

export const MY_ZONE_ACTIVITY_PANEL_AVATAR_CLASS =
  "relative h-[40px] w-[40px] shrink-0 self-center bg-[#d379f6]/25";

export const MY_ZONE_ACTIVITY_PANEL_CTA_CLASS =
  "inline-flex h-[30px] min-w-[4rem] w-[4rem] shrink-0 items-center justify-center whitespace-nowrap rounded-none border-0 bg-white px-2 py-0 text-center font-anton text-[9px] font-normal uppercase leading-none tracking-wide text-black transition-opacity hover:opacity-90 sm:min-w-[5.25rem] sm:w-[5.25rem] sm:text-[10px]";

export const MY_ZONE_ACTIVITY_PANEL_BODY_CLASS =
  "min-w-0 break-words [overflow-wrap:anywhere] font-chakra text-[12px] font-normal leading-[16px] tracking-normal text-white";

export const MY_ZONE_ACTIVITY_PANEL_NAME_CLASS =
  "font-anton text-[12px] font-normal leading-[16px] tracking-normal uppercase [overflow-wrap:anywhere]";

export const MY_ZONE_ACTIVITY_PANEL_TIME_CLASS =
  "whitespace-nowrap font-chakra text-[10px] font-normal leading-[16px] tracking-normal text-right text-[#c4b8d4]";

export const MY_ZONE_ACTIVITY_PANEL_TITLE_LINE_CLASS =
  "!block w-full min-w-0 break-words [overflow-wrap:anywhere] font-chakra text-[12px] font-normal leading-[16px] tracking-normal text-white";

export const MY_ZONE_ACTIVITY_PANEL_TITLE_USERNAME_CLASS =
  "font-anton text-[12px] font-normal leading-[16px] tracking-normal uppercase [overflow-wrap:anywhere]";

/** Min height matches avatar; row grows with wrapped title (drawer / activity / toast). */
const NOTIFICATION_ROW_HEIGHT_CLASS = "min-h-[30px]";
const NOTIFICATION_ALERT_ROW_HEIGHT_CLASS = "min-h-[30px]";

export function getNotificationRowShellClass(
  isUnread,
  includeUnreadRing = true,
  isAlertRow = false
) {
  const ring = includeUnreadRing && isUnread ? "ring-2 ring-inset ring-white/25" : "";
  const rowHeightClass = isAlertRow
    ? NOTIFICATION_ALERT_ROW_HEIGHT_CLASS
    : NOTIFICATION_ROW_HEIGHT_CLASS;
  return `flex w-full min-w-0 items-start gap-0.5 overflow-x-hidden pr-1 transition-[filter] sm:gap-1 sm:pr-2 bg-[#7234c1] ${rowHeightClass} ${ring} hover:brightness-105`;
}

/** Grid row: text column is minmax(0,1fr) so copy uses full width up to time/CTA (avoids flex min-content width traps). */
export function getNotificationRowGridShellClass(
  isUnread,
  includeUnreadRing = true,
  isAlertRow = false
) {
  const ring = includeUnreadRing && isUnread ? "ring-2 ring-inset ring-white/25" : "";
  const rowHeightClass = isAlertRow
    ? NOTIFICATION_ALERT_ROW_HEIGHT_CLASS
    : NOTIFICATION_ROW_HEIGHT_CLASS;
  return `grid w-full min-w-0 grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-0 overflow-x-hidden pr-1 transition-[filter] sm:pr-2 bg-[#7234c1] ${rowHeightClass} ${ring} hover:brightness-105`;
}
